import { IBannerInlinePlaceholderProps } from '@tekface/smart-activation';
import {
  CalculatePricePromotion,
  CalculatedPrice,
  SelectionPromotion,
} from '@teko/cart/v2';
import { DiscoveryProduct, ProductOptionsColumn } from '@teko/discovery';
import { Policy } from '@teko/ecommerce/mobile';
import { ProductCardProps } from '@teko/types/ecommerce';
import { Breadcrumb } from 'modules/search-page/shared';
import React, { Dispatch, SetStateAction, useContext } from 'react';
import { AddToCartPayload, ProductPrice, useDetailPage } from '../hooks';
import {
  ConvertedProduct,
  IGetProductReviewResponse,
  IRatingData,
  ProductResponse,
} from '../types';

export interface ProductDetailState {
  product?: ConvertedProduct;
  primitiveProduct?: DiscoveryProduct;
  frame?: string;
  onSelectVariant: (variant: ProductOptionsColumn) => void;
  stockAvailable?: number;
  isOutOfStock?: boolean;
  relatedProducts: ProductCardProps[];
  isCanAddToCard?: boolean;
  loading?: boolean;
  showFlashSale?: boolean;

  selectedPromotion?: SelectionPromotion | null;
  setSelectedPromotion?: (arg: SelectionPromotion | undefined) => void;
  selectPromotionLoading: boolean;
  price?: ProductPrice;
  // selectedPrice?: number | null;
  // setSelectedPrice?: (arg: number | null) => void;
  promotionBlocks: CalculatePricePromotion[];
  payloadAddToCart?: AddToCartPayload;
  setPayloadAddToCart?: Dispatch<SetStateAction<AddToCartPayload>>;
  promotionLoading?: boolean;
  actionReady?: boolean;
  showPopupCheckAge?: boolean;
  setShowPopupCheckAge: (value: boolean) => void;
  breadcrumbs?: Breadcrumb;
  inlineBanners?: IBannerInlinePlaceholderProps<{}>['banners'];
  isAllowInstallmentPayment?: boolean;
  showWarningPromotionWithInstallment: boolean;
  setShowWarningPromotionWithInstallment: (value: boolean) => void;
  selectedPromotionsNotApplyWithInstallment: SelectionPromotion[];
  minAmountByMonth?: number;
  variants?: CalculatedPrice['variants'];
  setIsReturnVariants?: Dispatch<SetStateAction<boolean>>;
  productRatingData: IRatingData;
  serverRating?: IGetProductReviewResponse;
  policies?: Policy[];
  salePolicies?: Policy[];
  otherPolicies?: Policy[];
  paymentPromotionPolicies?: Policy[];
}

export interface DetailProvider {
  children: React.ReactNode;
  serverProduct: ProductResponse;
  inlineBanners?: IBannerInlinePlaceholderProps<{}>['banners'];
}

const initialState: ProductDetailState = {
  onSelectVariant: () => {},
  promotionBlocks: [],
  relatedProducts: [],
  showPopupCheckAge: false,
  setShowPopupCheckAge: () => {},
  selectPromotionLoading: false,
  showWarningPromotionWithInstallment: false,
  setShowWarningPromotionWithInstallment: () => {},
  selectedPromotionsNotApplyWithInstallment: [],
  productRatingData: {
    reviews: [],
    hasMore: false,
    setFilterParams: () => {},
    loadMore: () => {},
    noFilter: false,
    numberOfReviewsRemaining: 0,
    setPage: () => {},
    isValidating: false,
    refreshReviewList: () => {},
  },
};

const ProductDetailStateContext =
  React.createContext<ProductDetailState>(initialState);

export const ProductDetailProvider: React.FunctionComponent<DetailProvider> = ({
  children,
  serverProduct,
  inlineBanners,
}: DetailProvider) => {
  const detailPage = useDetailPage(serverProduct);

  return (
    <>
      <ProductDetailStateContext.Provider
        value={{ ...detailPage, inlineBanners }}
      >
        {children}
      </ProductDetailStateContext.Provider>
    </>
  );
};

export function useProductDetailState(): ProductDetailState {
  const context = useContext(ProductDetailStateContext);
  if (!context) {
    throw new Error(
      'useProductDetailState must be inside a ProductDetailStateContext with a state value',
    );
  }
  return context;
}
